/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from 'contexts';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { Page, message } from 'components';
import { debounce } from 'lodash';
import getOrgUsers from 'api/UserController/org';
import { SupraLockboxActivity } from '../LockboxesActivityPage/types';
import QuickLockboxActivityTable from './QuickLockboxActivityTable';
import QuickLockboxesActivityRightHeader from './QuickLockboxesActivityRightHeader';
import { getSupraLockboxActivities } from '../../../api/SupraLockboxController';
import { LockboxesActivityFilterOptions } from '../../../api/get/getListingAccessActivities';

const QuickLockBoxesActivityPage = () => {
  const { auth } = useContext(AuthContext);
  const { search } = useLocation();
  const [lockboxActivities, setLockboxActivities] = useState<
    SupraLockboxActivity[]
  >([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoadComplete, setInitialLoadComplete] =
    useState<boolean>(false);
  const [total, setTotal] = useState(0);
  const [initialTotal, setInitialTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<LockboxesActivityFilterOptions>({
    ownerOrAssignee: [],
    ownerIds: [],
    assignee: [],
    lockboxIds: [],
    activities: [],
    lockboxSerialNumbers: [],
    startDate: null,
    endDate: null,
    userIds: [],
    listingIds: [],
    keyTypes: [],
  });
  const [accessibleUserIds, setAccessibleUserIds] = useState<string[]>([]);
  const [sortOptions, setSortOptions] = useState<{
    sort: string;
    order: 'descend' | 'ascend';
  }>({
    sort: '',
    order: 'descend',
  });
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (search) {
      setFilters(prev => ({
        ...prev,
        startDate: moment().startOf('day').toDate(),
        endDate: moment().endOf('day').toDate(),
      }));
    }
  }, [search]);

  useEffect(() => {
    async function fetchUsers() {
      let newAccessibleUserIds = accessibleUserIds;
      if (!initialLoadComplete) {
        try {
          const { users } = await getOrgUsers({
            pageNumber: 0,
            size: 1000,
          });
          newAccessibleUserIds = users.map(u => u.id);
        } catch (error) {
          message.error('Error fetching mls users:', error);
        }
        setAccessibleUserIds(newAccessibleUserIds);
        setInitialLoadComplete(true);
      }

      if (!filters.userIds?.length) {
        setFilters(prev => ({ ...prev, userIds: newAccessibleUserIds }));
      }
    }

    fetchUsers();
  }, [filters.userIds?.length]);

  useEffect(() => {
    (async () => {
      try {
        if (!initialLoadComplete || !filters.userIds?.length || loading) return;
        setLoading(true);
        const searchFilters = { ...filters };

        if (!filters.activities?.length) {
          searchFilters.activities = ['assignee_changed', 'owner_changed'];
        }
        const { activities, count } = await getSupraLockboxActivities({
          filters: searchFilters,
          sort: sortOptions,
          page,
        });
        setLockboxActivities(activities);
        setTotal(count);
        if (!initialTotal || initialTotal < count) setInitialTotal(count);
      } catch (err) {
        message.error('Something went wrong');
      } finally {
        setLoading(false);
      }
    })();
  }, [page, filters, sortOptions, initialLoadComplete]);

  const debouncedSearch = React.useMemo(
    () =>
      debounce(text => {
        setFilters(prev => ({ ...prev, search: text }));
      }, 300),
    [],
  );

  React.useEffect(() => () => {
      debouncedSearch.cancel();
    }, [debouncedSearch]);

  useEffect(() => {
    debouncedSearch(searchText);
  }, [searchText, debouncedSearch]);

  return (
    <Page
      type="filled"
      title={'Activity'}
      fillScreenHeight
      showBackButton
      headerExtra={
        <QuickLockboxesActivityRightHeader
          filters={filters}
          accessibleUserIds={accessibleUserIds}
          sort={sortOptions}
          page={page}
          setPage={setPage}
          total={total}
          initialTotal={initialTotal}
          searchText={searchText}
          setSearchText={setSearchText}
          setFilters={setFilters}
          isLoading={loading}
        />
      }
    >
      <QuickLockboxActivityTable
        filters={filters}
        sortOptions={sortOptions}
        setFilters={setFilters}
        setSortOptions={setSortOptions}
        activities={lockboxActivities}
        loading={loading}
        totalCount={total}
        page={page}
        setPage={setPage}
      />
    </Page>
  );
};

export default QuickLockBoxesActivityPage;
